import { z } from 'zod'
import type { MenuTagValue } from './menu-tags'
import type { Axis } from './taxonomy'

/**
 * 식품 축 속성 꼬리표 (2026-11-01, 대표 확정 명세 recipecat-food-category-spec-20261101.md 의 "속성 태그").
 * 메뉴 꼬리표(menu-tags.ts)와 같은 방식이다. 대분류·세부 나무가 "무엇을 만드는가" 라면 꼬리표는 "어떤 조건으로" 다.
 * - 축 2개, 값 10개. 속성(attribute) 7: 비건, 식물성, 제로·저당·저칼로리, 글루텐프리, 유기농·클린라벨, 고단백, 키토·저탄수.
 *   보관 온도(storage) 3: 냉동, 상온, 냉장 (하나만 고른다).
 * - 값 모양은 메뉴 꼬리표 값과 같다 (ko·en·ja·zh 라벨 + 프롬프트 hint). 비건·글루텐프리는 메뉴 식이 제한 값과 id 를 맞췄다.
 * - 선택 칸이라 비워도 지금과 같다. 서버는 생성 프롬프트 사용자 메시지에만 쓴다(productTagsPromptLines).
 *   대·중분류 골격과 categoryId 는 꼬리표 때문에 바뀌지 않는다.
 * - 식품 축 create 모드에만 쓴다. 다른 축이면 normalizeProductTags 가 비운다. 옛 앱은 보내지 않고, 옛 서버는 모르는 칸이라 버린다.
 * - 값 id 는 한 번 정하면 바꾸지 않는다 (저장된 input 이 이 id 를 들고 있다). 라벨·hint 는 바꿔도 된다.
 */
export const PRODUCT_TAG_AXES = ['attribute', 'storage'] as const
export type ProductTagAxis = (typeof PRODUCT_TAG_AXES)[number]

/** 메뉴 꼬리표 값에서 라벨 4개와 hint 만 쓴다 (메뉴 쪽 묶음·숨김 칸은 식품 축에 없다) */
export type ProductTagValue = Pick<MenuTagValue, 'id' | 'ko' | 'en' | 'ja' | 'zh' | 'hint'>

const v = (
  id: string,
  ko: string,
  en: string,
  ja: string,
  zh: string,
  hint: string,
): ProductTagValue => ({ id, ko, en, ja, zh, hint })

export const PRODUCT_TAGS: Record<ProductTagAxis, ProductTagValue[]> = {
  // ── 속성 7 (명세 순서) ──
  attribute: [
    v(
      'vegan',
      '비건',
      'Vegan',
      'ヴィーガン',
      '纯素',
      '고기·생선·해산물·달걀·유제품·꿀·젤라틴과 동물 유래 첨가물(카민, 셸락, 유청, 동물성 효소)을 쓰지 않는다. 대체 원료와 공급사 원료 증명, 같은 라인의 교차오염 관리를 steps 의 qc 에 적는다.',
    ),
    v(
      'plant-based',
      '식물성',
      'Plant-based',
      'プラントベース',
      '植物基',
      '주원료(단백·유지·유제품 자리)를 식물성으로 바꾼다. 소량 첨가물까지 막는 비건과 달리 어떤 원료를 무엇으로 바꿨는지가 축이고, 바꾼 원료가 맛·식감·원가에 주는 차이를 적는다.',
    ),
    v(
      'zero-low-sugar',
      '제로·저당·저칼로리',
      'Zero sugar, low sugar & low calorie',
      'ゼロ・低糖・低カロリー',
      '零糖·低糖·低卡',
      '당류와 열량을 줄인다. 대체 감미료(알룰로스, 에리스리톨, 스테비아, 수크랄로스)를 고르고, 당이 빠지며 생기는 보존·식감·색 문제(수분활성도, 바디감, 갈변)를 보완한다. 1회 제공량의 당류 g 과 열량을 함께 맞춘다.',
    ),
    v(
      'gluten-free',
      '글루텐프리',
      'Gluten-free',
      'グルテンフリー',
      '无麸质',
      '밀·보리·호밀과 그 가공품(일반 간장, 밀 전분, 맥아)을 쓰지 않고 쌀가루·전분·글루텐프리 간장으로 바꾼다. 글루텐이 빠진 반죽의 결착(하이드로콜로이드)과 같은 시설의 교차오염 관리가 축.',
    ),
    v(
      'organic-clean',
      '유기농·클린라벨',
      'Organic & clean label',
      'オーガニック・クリーンラベル',
      '有机·清洁标签',
      '유기 원료를 쓰고, 합성 보존료·착색료·향료·감미료 대신 원재료 이름으로 읽히는 짧은 원재료 목록을 목표로 한다. 첨가물을 빼면서 짧아지는 소비기한, 유기 원료 단가와 수급이 축.',
    ),
    v(
      'high-protein',
      '고단백',
      'High-protein',
      '高たんぱく',
      '高蛋白',
      '단백질 원료(유청·대두·완두 단백, 닭가슴살, 달걀흰자)로 1회 제공량당 단백질을 높인다. 단백질이 늘며 딱딱하거나 텁텁해지는 식감과 단백 원료 단가가 축. 1회 제공량 단백질 g 목표를 정한다.',
    ),
    v(
      'keto',
      '키토·저탄수',
      'Keto & low-carb',
      'ケト・低糖質',
      '生酮·低碳水',
      '당·전분·곡물을 줄이고 지방·단백질과 식이섬유(이눌린, 차전자피), 대체 감미료로 채운다. 탄수화물에서 식이섬유·당알코올을 뺀 순탄수를 1회 제공량 기준으로 계산하고, 전분이 빠져 생기는 결착·부피 문제를 보완한다.',
    ),
  ],
  // ── 보관 온도 3 (하나만) ──
  storage: [
    v(
      'frozen',
      '냉동',
      'Frozen',
      '冷凍',
      '冷冻',
      '영하 18℃ 이하로 유통한다. 급속동결과 해동·재가열 뒤 식감, 얼음 결정과 냉동 건조를 막는 포장이 축. product.storage 를 냉동으로 둔다.',
    ),
    v(
      'ambient',
      '상온',
      'Shelf-stable (ambient)',
      '常温',
      '常温',
      '냉장·냉동 없이 상온·실온에서 유통한다. 평형 pH 4.6 이하, 수분활성도 0.85 이하, 레토르트·무균 충전, 건조 중 어느 장벽으로 상온 유통을 성립시키는지 정해 공정과 qc 에 적는다. product.storage 를 상온으로 둔다.',
    ),
    v(
      'chilled',
      '냉장',
      'Chilled',
      '冷蔵',
      '冷藏',
      '0~10℃ 로 유통한다. 살균 강도와 짧은 소비기한, 저온에서도 자라는 균(리스테리아 등) 관리가 축. product.storage 를 냉장으로 둔다.',
    ),
  ],
}

/** 축마다 고를 수 있는 최대 개수 (보관 온도는 하나만) */
export const PRODUCT_TAG_LIMITS: Record<ProductTagAxis, number> = {
  attribute: PRODUCT_TAGS.attribute.length,
  storage: 1,
}

/** 축 라벨 (화면 작은 제목·프롬프트 줄 머리) */
const AXIS_LABELS: Record<ProductTagAxis, { ko: string; en: string; ja: string; zh: string }> = {
  attribute: { ko: '속성', en: 'Attributes', ja: '属性', zh: '属性' },
  storage: { ko: '보관 온도', en: 'Storage', ja: '保存温度', zh: '储存温度' },
}

/** 축별 값 id 목록. 비어 있는 축은 키가 없다 */
export type ProductTags = Partial<Record<ProductTagAxis, string[]>>

const tagIds = z.array(z.string().min(1).max(40)).max(10).optional()

/**
 * 입력 검증. 개수 상한은 느슨하게(10), 실제 상한·충돌 정리는 normalizeProductTags 가 한다.
 * 모르는 축 키는 zod object 가 버린다
 */
export const productTagsSchema = z.object({
  attribute: tagIds,
  storage: tagIds,
})

export function findProductTag(axis: ProductTagAxis, id: string): ProductTagValue | undefined {
  return PRODUCT_TAGS[axis]?.find((x) => x.id === id)
}

function pick(
  labels: { ko: string; en: string; ja: string; zh: string },
  language: string,
): string {
  if (language === 'ko' || language === 'ja' || language === 'zh') return labels[language]
  return labels.en
}

/** 값 라벨. 모르는 id 는 id 그대로 돌려준다 */
export function productTagLabel(axis: ProductTagAxis, id: string, language: string): string {
  const tag = findProductTag(axis, id)
  return tag ? pick(tag, language) : id
}

export function productTagAxisLabel(axis: ProductTagAxis, language: string): string {
  const labels = AXIS_LABELS[axis]
  return labels ? pick(labels, language) : axis
}

/**
 * 꼬리표 정리. 서버(생성)와 앱(고르기 카드)이 같은 함수를 쓴다.
 * 1. 식품 축이 아니면 {} 2. 모르는 축·값은 버리고 중복은 하나만 3. 비건이 있으면 식물성을 지운다 (비건이 더 좁은 조건)
 * 4. 축 상한을 넘으면 앞에서부터 그 개수만 5. 빈 축은 키를 지우고 PRODUCT_TAGS 값 순서로 정렬 (같은 선택이면 같은 결과)
 * 저장된 JSON 을 그대로 받을 수 있게 모양이 틀린 입력(배열 아님, 문자열 아님)도 오류 없이 버린다
 */
export function normalizeProductTags(
  tags: ProductTags | null | undefined,
  axis: Axis | null | undefined,
): ProductTags {
  if (axis !== 'product' || !tags || typeof tags !== 'object' || Array.isArray(tags)) return {}
  const picked = {} as Record<ProductTagAxis, string[]>
  for (const a of PRODUCT_TAG_AXES) {
    const raw: unknown = (tags as Record<string, unknown>)[a]
    const ids = Array.isArray(raw) ? raw : []
    const seen = new Set<string>()
    picked[a] = ids.filter(
      (id): id is string =>
        typeof id === 'string' && !seen.has(id) && !!findProductTag(a, id) && (seen.add(id), true),
    )
  }
  if (picked.attribute.includes('vegan'))
    picked.attribute = picked.attribute.filter((id) => id !== 'plant-based')
  const out: ProductTags = {}
  for (const a of PRODUCT_TAG_AXES) {
    const kept = new Set(picked[a].slice(0, PRODUCT_TAG_LIMITS[a]))
    if (kept.size === 0) continue
    out[a] = PRODUCT_TAGS[a].filter((x) => kept.has(x.id)).map((x) => x.id)
  }
  return out
}

export function countProductTags(tags: ProductTags | null | undefined): number {
  if (!tags) return 0
  return PRODUCT_TAG_AXES.reduce((n, a) => {
    const ids = tags[a]
    return n + (Array.isArray(ids) ? ids.length : 0)
  }, 0)
}

/**
 * 속성 꼬리표의 표시 단정 금지 (메뉴 DIET_CLAIM_LINE 과 같은 이유). 배합만으로는 비건·글루텐프리·저당·고단백·유기 같은
 * 표시를 할 수 없다 (함량 기준, 인증, 같은 시설 혼입 주의 문구). 라벨 초안과 baseNote 에 인증처럼 쓰지 않게 한다
 */
export const PRODUCT_CLAIM_LINE =
  '비건, 식물성, 제로·저당·저칼로리, 글루텐프리, 유기농·클린라벨, 고단백, 키토·저탄수는 배합 목표다. complianceSummary·baseNote 에 인증·강조표시 문구로 단정하지 않는다. 표시하려면 판매 시장의 기준(영양성분 강조표시 함량, 글루텐 함량, 유기·비건 인증)을 실측과 인증으로 확인해야 한다는 것을 caution 에 적는다.'

/**
 * 프롬프트용 줄 목록 (한국어). 빈 꼬리표면 [].
 * 머리 한 줄, 값마다 "- 축: 값. hint", 속성이 있으면 지키라는 줄과 표시 단정 금지 줄(PRODUCT_CLAIM_LINE),
 * 보관 온도가 있으면 유통 설계 줄, 마지막에 미국 시장 치환 줄.
 * 모르는 값은 건너뛴다 (정리 전 값을 받아도 프롬프트에 id 가 새지 않게)
 */
export function productTagsPromptLines(tags: ProductTags | null | undefined): string[] {
  if (!tags) return []
  const lines: string[] = []
  for (const a of PRODUCT_TAG_AXES) {
    for (const id of tags[a] ?? []) {
      const tag = findProductTag(a, id)
      if (tag) lines.push(`- ${productTagAxisLabel(a, 'ko')}: ${tag.ko}. ${tag.hint}`)
    }
  }
  if (lines.length === 0) return []
  const has = (a: ProductTagAxis) => (tags[a] ?? []).some((id) => !!findProductTag(a, id))
  return [
    '[속성 꼬리표: 사용자가 고른 제품 조건. 대·중분류는 그대로 두고 배합·공정·포장·원가에 반영한다]',
    ...lines,
    has('attribute') ? '속성은 지켜야 하는 설계 조건이다. 어기는 원료를 쓰지 않는다.' : '',
    has('attribute') ? PRODUCT_CLAIM_LINE : '',
    has('storage')
      ? '보관 온도는 product.storage 와 유통 설계(살균·포장·소비기한)에 그대로 쓴다. 다른 온도대로 바꾸지 않는다.'
      : '',
    '판매 시장이 미국이면 온도·표시 기준은 FDA/USDA 기준으로 바꿔 쓴다.',
  ].filter(Boolean)
}
